import { useCallback, useEffect, useRef } from "react";

import { trackGAEvent } from "../util/analytics";
import { useAiAgentChatGaContext } from "./use-ai-agent-chat-ga-context";

export interface AiAgentChatTracking {
  trackMessageSent: () => void;
  trackLinkClick: (linkUrl: string, linkText?: string) => void;
}

export function useAiAgentChatTracking(isOpen: boolean): AiAgentChatTracking {
  const { page_path, userStatus, hashUserCpf } = useAiAgentChatGaContext();
  const hasInteractionRef = useRef(false);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (!wasOpenRef.current && isOpen) {
      hasInteractionRef.current = false;

      trackGAEvent("open_ai_agent_chat", {
        page_path,
        userStatus,
        hashUserCpf,
      });
    } else if (wasOpenRef.current && !isOpen) {
      trackGAEvent("close_ai_agent_chat", {
        page_path,
        userStatus,
        hashUserCpf,
        has_interaction: hasInteractionRef.current,
      });
    }

    wasOpenRef.current = isOpen;
  }, [hashUserCpf, isOpen, page_path, userStatus]);

  const trackMessageSent = useCallback(() => {
    hasInteractionRef.current = true;

    trackGAEvent("message_sent_to_ai_agent", {
      page_path,
      userStatus,
      hashUserCpf,
    });
  }, [hashUserCpf, page_path, userStatus]);

  const trackLinkClick = useCallback(
    (linkUrl: string, linkText?: string) => {
      hasInteractionRef.current = true;

      trackGAEvent("ai_agent_chat_link_click", {
        page_path,
        userStatus,
        hashUserCpf,
        link_url: linkUrl,
        link_text: linkText,
        nonInteraction: false,
        transport: "beacon",
      });
    },
    [hashUserCpf, page_path, userStatus]
  );

  return {
    trackMessageSent,
    trackLinkClick,
  };
}
